import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useEditor } from '../../context/EditorContext';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  ZoomIn,
  ZoomOut,
  RotateCw,
  FlipHorizontal,
  FlipVertical,
  Crop,
  Wand2,
  Paintbrush,
  Eraser,
  Download,
  X,
  Loader2,
  Sun,
  Contrast,
  Droplets,
  Move,
  Lock,
  Unlock,
  Check,
  Maximize2,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PhotoEditorModal = () => {
  const { state, dispatch } = useEditor();
  const { photoEditorImage } = state;
  const canvasRef = useRef(null);
  const imageContainerRef = useRef(null);
  const cropCanvasRef = useRef(null);
  
  // Basic adjustments
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [flipH, setFlipH] = useState(false);
  const [flipV, setFlipV] = useState(false);
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [saturation, setSaturation] = useState(100);
  
  // Tools & states
  const [activeTool, setActiveTool] = useState('adjust');
  const [isRemovingBg, setIsRemovingBg] = useState(false);
  const [brushSize, setBrushSize] = useState(20);
  const [imageData, setImageData] = useState(null);
  const [originalImageData, setOriginalImageData] = useState(null);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  
  // Pan state
  const [isPanning, setIsPanning] = useState(false);
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  
  // Crop state
  const [isCropping, setIsCropping] = useState(false);
  const [cropArea, setCropArea] = useState({ x: 0, y: 0, width: 100, height: 100 });
  const [cropDragType, setCropDragType] = useState(null); // 'move', 'nw', 'ne', 'sw', 'se', 'n', 's', 'e', 'w'
  const [cropDragStart, setCropDragStart] = useState({ x: 0, y: 0, crop: null });
  
  // Resize state
  const [resizeWidth, setResizeWidth] = useState(0);
  const [resizeHeight, setResizeHeight] = useState(0);
  const [lockAspectRatio, setLockAspectRatio] = useState(true);
  const [aspectRatio, setAspectRatio] = useState(1);
  
  // Load image and get dimensions
  useEffect(() => {
    if (photoEditorImage?.properties?.src) {
      const src = photoEditorImage.properties.src;
      setImageData(src);
      setOriginalImageData(src);
      
      // Load image to get dimensions
      const img = new Image();
      img.onload = () => {
        setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
        setResizeWidth(img.naturalWidth);
        setResizeHeight(img.naturalHeight);
        setAspectRatio(img.naturalWidth / img.naturalHeight);
        
        // Initialize crop area to full image
        setCropArea({ x: 0, y: 0, width: img.naturalWidth, height: img.naturalHeight });
      };
      img.src = src;
    }
  }, [photoEditorImage]);
  
  const handleClose = () => {
    dispatch({ type: 'HIDE_PHOTO_EDITOR' });
    resetAllStates();
  };
  
  const resetAllStates = () => {
    setBrightness(100);
    setContrast(100);
    setSaturation(100);
    setRotation(0);
    setFlipH(false);
    setFlipV(false);
    setZoom(1);
    setPanPosition({ x: 0, y: 0 });
    setIsCropping(false);
  };
  
  const handleApply = () => {
    if (photoEditorImage && imageData) {
      const prev = photoEditorImage.properties || {};
      // A new picture (cropped / resized / background removed here) replaces the old one in the frame: an in-place crop
      // rectangle (Picture Tools ▸ Crop) described where the OLD picture sat, so it is dropped and the new picture fills
      // the frame the usual way. Filter-only edits keep the in-place crop.
      const replaced = imageData !== prev.src;
      dispatch({
        type: 'UPDATE_OBJECT',
        payload: {
          id: photoEditorImage.id,
          data: {
            properties: {
              ...prev,
              ...(replaced ? { crop: undefined } : {}),
              src: imageData,
              filters: {
                brightness,
                contrast,
                saturation,
              },
              rotation,
              flipH,
              flipV,
            },
          },
        },
      });
    }
    handleClose();
    toast.success('Image updated');
  };
  
  const handleRemoveBackground = async () => {
    if (!imageData) return;
    
    setIsRemovingBg(true);
    try {
      let base64Data = imageData;
      if (imageData.startsWith('http')) {
        const response = await fetch(imageData);
        const blob = await response.blob();
        base64Data = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result.split(',')[1]);
          reader.readAsDataURL(blob);
        });
      } else if (imageData.startsWith('data:')) {
        base64Data = imageData.split(',')[1];
      }
      
      const result = await axios.post(`${API}/images/remove-background`, {
        image_base64: base64Data,
      });
      
      if (result.data.success) {
        setImageData(`data:image/png;base64,${result.data.image_base64}`);
        toast.success('Background removed successfully');
      }
    } catch (error) {
      console.error('Background removal error:', error);
      toast.error('Failed to remove background. Please try again.');
    } finally {
      setIsRemovingBg(false);
    }
  };
  
  const handleDownload = () => {
    if (!imageData) return;
    
    const link = document.createElement('a');
    link.href = imageData;
    link.download = 'edited-image.png';
    link.click();
    toast.success('Image downloaded');
  };
  
  const resetAdjustments = () => {
    setBrightness(100);
    setContrast(100);
    setSaturation(100);
    setRotation(0);
    setFlipH(false);
    setFlipV(false);
    setZoom(1);
    setPanPosition({ x: 0, y: 0 });
  };
  
  // ========== PAN HANDLERS ==========
  const handlePanStart = useCallback((e) => {
    if (zoom <= 1 || isCropping) return;
    setIsPanning(true);
    setPanStart({
      x: e.clientX - panPosition.x,
      y: e.clientY - panPosition.y,
    });
  }, [zoom, panPosition, isCropping]);
  
  const handlePanMove = useCallback((e) => {
    if (!isPanning) return;
    const newX = e.clientX - panStart.x;
    const newY = e.clientY - panStart.y;
    
    // Limit pan based on zoom level
    const maxPan = (zoom - 1) * 200;
    setPanPosition({
      x: Math.max(-maxPan, Math.min(maxPan, newX)),
      y: Math.max(-maxPan, Math.min(maxPan, newY)),
    });
  }, [isPanning, panStart, zoom]);
  
  const handlePanEnd = useCallback(() => {
    setIsPanning(false);
  }, []);
  
  // Reset pan when zoom changes below 1
  useEffect(() => {
    if (zoom <= 1) {
      setPanPosition({ x: 0, y: 0 });
    }
  }, [zoom]);
  
  // ========== CROP HANDLERS ==========
  const startCropMode = () => {
    setIsCropping(true);
    // Initialize crop area to 80% of the displayed image
    const margin = 0.1;
    setCropArea({
      x: imageDimensions.width * margin,
      y: imageDimensions.height * margin,
      width: imageDimensions.width * (1 - margin * 2),
      height: imageDimensions.height * (1 - margin * 2),
    });
  };
  
  const cancelCrop = () => {
    setIsCropping(false);
  };
  
  const applyCrop = async () => {
    if (!imageData) return;
    
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = imageData;
      });
      
      const canvas = document.createElement('canvas');
      canvas.width = cropArea.width;
      canvas.height = cropArea.height;
      const ctx = canvas.getContext('2d');
      
      ctx.drawImage(
        img,
        cropArea.x,
        cropArea.y,
        cropArea.width,
        cropArea.height,
        0,
        0,
        cropArea.width,
        cropArea.height
      );
      
      const croppedData = canvas.toDataURL('image/png');
      setImageData(croppedData);
      setImageDimensions({ width: cropArea.width, height: cropArea.height });
      setResizeWidth(cropArea.width);
      setResizeHeight(cropArea.height);
      setAspectRatio(cropArea.width / cropArea.height);
      setIsCropping(false);
      toast.success('Image cropped successfully');
    } catch (error) {
      console.error('Crop error:', error);
      toast.error('Failed to crop image');
    }
  };
  
  const handleCropMouseDown = (e, type) => {
    e.stopPropagation();
    setCropDragType(type);
    setCropDragStart({
      x: e.clientX,
      y: e.clientY,
      crop: { ...cropArea },
    });
  };
  
  const handleCropMouseMove = useCallback((e) => {
    if (!cropDragType || !cropDragStart.crop) return;
    
    const containerRect = imageContainerRef.current?.getBoundingClientRect();
    if (!containerRect) return;
    
    const img = imageContainerRef.current?.querySelector('img');
    if (!img) return;
    
    const imgRect = img.getBoundingClientRect();
    const scaleX = imageDimensions.width / imgRect.width;
    const scaleY = imageDimensions.height / imgRect.height;
    
    const deltaX = (e.clientX - cropDragStart.x) * scaleX;
    const deltaY = (e.clientY - cropDragStart.y) * scaleY;
    
    const { crop } = cropDragStart;
    let newCrop = { ...crop };
    const minSize = 20;
    
    if (cropDragType === 'move') {
      newCrop.x = Math.max(0, Math.min(imageDimensions.width - crop.width, crop.x + deltaX));
      newCrop.y = Math.max(0, Math.min(imageDimensions.height - crop.height, crop.y + deltaY));
    } else {
      // Handle resize from corners and edges
      if (cropDragType.includes('w')) {
        const newX = crop.x + deltaX;
        const newWidth = crop.width - deltaX;
        if (newX >= 0 && newWidth >= minSize) {
          newCrop.x = newX;
          newCrop.width = newWidth;
        }
      }
      if (cropDragType.includes('e')) {
        const newWidth = crop.width + deltaX;
        if (crop.x + newWidth <= imageDimensions.width && newWidth >= minSize) {
          newCrop.width = newWidth;
        }
      }
      if (cropDragType.includes('n')) {
        const newY = crop.y + deltaY;
        const newHeight = crop.height - deltaY;
        if (newY >= 0 && newHeight >= minSize) {
          newCrop.y = newY;
          newCrop.height = newHeight;
        }
      }
      if (cropDragType.includes('s')) {
        const newHeight = crop.height + deltaY;
        if (crop.y + newHeight <= imageDimensions.height && newHeight >= minSize) {
          newCrop.height = newHeight;
        }
      }
    }
    
    setCropArea(newCrop);
  }, [cropDragType, cropDragStart, imageDimensions]);
  
  const handleCropMouseUp = useCallback(() => {
    setCropDragType(null);
    setCropDragStart({ x: 0, y: 0, crop: null });
  }, []);
  
  // Add global mouse listeners for crop dragging
  useEffect(() => {
    if (isCropping) {
      window.addEventListener('mousemove', handleCropMouseMove);
      window.addEventListener('mouseup', handleCropMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleCropMouseMove);
        window.removeEventListener('mouseup', handleCropMouseUp);
      };
    }
  }, [isCropping, handleCropMouseMove, handleCropMouseUp]);
  
  // ========== RESIZE HANDLERS ==========
  const handleWidthChange = (value) => {
    const newWidth = parseInt(value) || 0;
    setResizeWidth(newWidth);
    if (lockAspectRatio && aspectRatio) {
      setResizeHeight(Math.round(newWidth / aspectRatio));
    }
  };
  
  const handleHeightChange = (value) => {
    const newHeight = parseInt(value) || 0;
    setResizeHeight(newHeight);
    if (lockAspectRatio && aspectRatio) {
      setResizeWidth(Math.round(newHeight * aspectRatio));
    }
  };
  
  const applyResize = async () => {
    if (!imageData || resizeWidth <= 0 || resizeHeight <= 0) return;
    
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = imageData;
      });
      
      const canvas = document.createElement('canvas');
      canvas.width = resizeWidth;
      canvas.height = resizeHeight;
      const ctx = canvas.getContext('2d');
      
      // Use better image smoothing
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      
      ctx.drawImage(img, 0, 0, resizeWidth, resizeHeight);
      
      const resizedData = canvas.toDataURL('image/png');
      setImageData(resizedData);
      setImageDimensions({ width: resizeWidth, height: resizeHeight });
      setAspectRatio(resizeWidth / resizeHeight);
      toast.success(`Image resized to ${resizeWidth} × ${resizeHeight}`);
    } catch (error) {
      console.error('Resize error:', error);
      toast.error('Failed to resize image');
    }
  };
  
  const resetToOriginal = () => {
    if (originalImageData) {
      setImageData(originalImageData);
      const img = new Image();
      img.onload = () => {
        setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
        setResizeWidth(img.naturalWidth);
        setResizeHeight(img.naturalHeight);
        setAspectRatio(img.naturalWidth / img.naturalHeight);
        setCropArea({ x: 0, y: 0, width: img.naturalWidth, height: img.naturalHeight });
      };
      img.src = originalImageData;
      // Reset all adjustments
      setBrightness(100);
      setContrast(100);
      setSaturation(100);
      setRotation(0);
      setFlipH(false);
      setFlipV(false);
      setZoom(1);
      setPanPosition({ x: 0, y: 0 });
      setIsCropping(false);
      toast.success('Reset to original image');
    }
  };
  
  // Get cursor style based on state
  const getContainerCursor = () => {
    if (isCropping) return 'default';
    if (zoom > 1) return isPanning ? 'grabbing' : 'grab';
    return 'default';
  };
  
  const imageStyle = {
    filter: `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%)`,
    transform: `scale(${zoom}) rotate(${rotation}deg) scaleX(${flipH ? -1 : 1}) scaleY(${flipV ? -1 : 1}) translate(${panPosition.x / zoom}px, ${panPosition.y / zoom}px)`,
    transition: isPanning ? 'none' : 'transform 0.2s ease',
  };
  
  // Render crop overlay
  const renderCropOverlay = () => {
    if (!isCropping || !imageContainerRef.current) return null;
    
    const img = imageContainerRef.current.querySelector('img');
    if (!img) return null;
    
    const imgRect = img.getBoundingClientRect();
    const containerRect = imageContainerRef.current.getBoundingClientRect();
    
    // Calculate crop overlay position relative to displayed image
    const scaleX = imgRect.width / imageDimensions.width;
    const scaleY = imgRect.height / imageDimensions.height;
    
    const displayCrop = {
      x: (imgRect.left - containerRect.left) + (cropArea.x * scaleX),
      y: (imgRect.top - containerRect.top) + (cropArea.y * scaleY),
      width: cropArea.width * scaleX,
      height: cropArea.height * scaleY,
    };
    
    const handleSize = 10;
    
    return (
      <div className="absolute inset-0 pointer-events-none">
        {/* Dark overlay outside crop area */}
        <div className="absolute inset-0 bg-black/60 pointer-events-auto" style={{ clipPath: `polygon(
          0% 0%, 100% 0%, 100% 100%, 0% 100%, 0% 0%,
          ${displayCrop.x}px ${displayCrop.y}px,
          ${displayCrop.x}px ${displayCrop.y + displayCrop.height}px,
          ${displayCrop.x + displayCrop.width}px ${displayCrop.y + displayCrop.height}px,
          ${displayCrop.x + displayCrop.width}px ${displayCrop.y}px,
          ${displayCrop.x}px ${displayCrop.y}px
        )`}} />
        
        {/* Crop area border */}
        <div
          className="absolute border-2 border-white pointer-events-auto cursor-move"
          style={{
            left: displayCrop.x,
            top: displayCrop.y,
            width: displayCrop.width,
            height: displayCrop.height,
          }}
          onMouseDown={(e) => handleCropMouseDown(e, 'move')}
        >
          {/* Grid lines */}
          <div className="absolute inset-0 grid grid-cols-3 grid-rows-3 pointer-events-none">
            {[...Array(4)].map((_, i) => (
              <div key={`v-${i}`} className="absolute top-0 bottom-0 w-px bg-white/40" style={{ left: `${(i + 1) * 25}%` }} />
            ))}
            {[...Array(4)].map((_, i) => (
              <div key={`h-${i}`} className="absolute left-0 right-0 h-px bg-white/40" style={{ top: `${(i + 1) * 25}%` }} />
            ))}
          </div>
          
          {/* Resize handles */}
          {/* Corners */}
          <div className="absolute bg-white border border-zinc-400 cursor-nw-resize pointer-events-auto" 
            style={{ width: handleSize, height: handleSize, left: -handleSize/2, top: -handleSize/2 }}
            onMouseDown={(e) => handleCropMouseDown(e, 'nw')} />
          <div className="absolute bg-white border border-zinc-400 cursor-ne-resize pointer-events-auto"
            style={{ width: handleSize, height: handleSize, right: -handleSize/2, top: -handleSize/2 }}
            onMouseDown={(e) => handleCropMouseDown(e, 'ne')} />
          <div className="absolute bg-white border border-zinc-400 cursor-sw-resize pointer-events-auto"
            style={{ width: handleSize, height: handleSize, left: -handleSize/2, bottom: -handleSize/2 }}
            onMouseDown={(e) => handleCropMouseDown(e, 'sw')} />
          <div className="absolute bg-white border border-zinc-400 cursor-se-resize pointer-events-auto"
            style={{ width: handleSize, height: handleSize, right: -handleSize/2, bottom: -handleSize/2 }}
            onMouseDown={(e) => handleCropMouseDown(e, 'se')} />
          
          {/* Edges */}
          <div className="absolute bg-white border border-zinc-400 cursor-n-resize pointer-events-auto"
            style={{ width: handleSize, height: handleSize, left: '50%', marginLeft: -handleSize/2, top: -handleSize/2 }}
            onMouseDown={(e) => handleCropMouseDown(e, 'n')} />
          <div className="absolute bg-white border border-zinc-400 cursor-s-resize pointer-events-auto"
            style={{ width: handleSize, height: handleSize, left: '50%', marginLeft: -handleSize/2, bottom: -handleSize/2 }}
            onMouseDown={(e) => handleCropMouseDown(e, 's')} />
          <div className="absolute bg-white border border-zinc-400 cursor-w-resize pointer-events-auto"
            style={{ width: handleSize, height: handleSize, left: -handleSize/2, top: '50%', marginTop: -handleSize/2 }}
            onMouseDown={(e) => handleCropMouseDown(e, 'w')} />
          <div className="absolute bg-white border border-zinc-400 cursor-e-resize pointer-events-auto"
            style={{ width: handleSize, height: handleSize, right: -handleSize/2, top: '50%', marginTop: -handleSize/2 }}
            onMouseDown={(e) => handleCropMouseDown(e, 'e')} />
        </div>
        
        {/* Crop dimensions display */}
        <div className="absolute bg-black/80 text-white text-xs px-2 py-1 rounded"
          style={{ left: displayCrop.x, top: displayCrop.y - 24 }}>
          {Math.round(cropArea.width)} × {Math.round(cropArea.height)}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={state.showPhotoEditor} onOpenChange={handleClose}>
      <DialogContent className="!flex !flex-col max-w-5xl h-[90vh] bg-background border-border p-0" aria-describedby="photo-editor-description">
        <DialogHeader className="p-4 border-b border-border">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-lg font-outfit text-foreground">Photo Editor</DialogTitle>
            <p id="photo-editor-description" className="sr-only">
              Edit your image with zoom, crop, resize, and adjustment tools
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={resetToOriginal}
                className="text-muted-foreground hover:text-foreground"
                data-testid="reset-original-btn"
              >
                Reset Original
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDownload}
                className="text-muted-foreground hover:text-foreground"
              >
                <Download className="w-4 h-4 mr-1" />
                Export
              </Button>
              <Button
                onClick={handleApply}
                className="bg-violet-600 hover:bg-violet-700"
                size="sm"
                data-testid="apply-changes-btn"
              >
                Apply Changes
              </Button>
            </div>
          </div>
        </DialogHeader>
        
        <div className="flex-1 overflow-hidden" style={{ display: 'flex', flexDirection: 'row' }}>
          {/* Main Canvas Area */}
          <div 
            ref={imageContainerRef}
            className="flex-1 flex items-center justify-center bg-muted/30 overflow-hidden p-4 relative"
            style={{ cursor: getContainerCursor() }}
            onMouseDown={!isCropping ? handlePanStart : undefined}
            onMouseMove={!isCropping ? handlePanMove : undefined}
            onMouseUp={!isCropping ? handlePanEnd : undefined}
            onMouseLeave={!isCropping ? handlePanEnd : undefined}
          >
            {imageData ? (
              <div className="relative max-w-full max-h-full overflow-visible select-none">
                <img
                  ref={canvasRef}
                  src={imageData}
                  alt="Editing"
                  style={imageStyle}
                  className="max-w-full max-h-[60vh] object-contain"
                  draggable={false}
                  data-testid="photo-editor-image"
                />
                {isRemovingBg && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                    <div className="text-center">
                      <Loader2 className="w-8 h-8 animate-spin text-violet-500 mx-auto" />
                      <p className="mt-2 text-sm text-foreground">Removing background...</p>
                      <p className="text-xs text-muted-foreground">This may take a minute</p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-muted-foreground">No image loaded</div>
            )}
            
            {/* Crop Overlay */}
            {isCropping && renderCropOverlay()}
            
            {/* Crop action buttons */}
            {isCropping && (
              <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={cancelCrop}
                  className="bg-background border-border"
                  data-testid="cancel-crop-btn"
                >
                  <X className="w-4 h-4 mr-1" />
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={applyCrop}
                  className="bg-violet-600 hover:bg-violet-700"
                  data-testid="apply-crop-btn"
                >
                  <Check className="w-4 h-4 mr-1" />
                  Apply Crop
                </Button>
              </div>
            )}
            
            {/* Zoom & Pan indicator */}
            {zoom > 1 && !isCropping && (
              <div className="absolute bottom-4 left-4 bg-background/90 border border-border rounded-lg px-3 py-2 flex items-center gap-2 text-sm">
                <Move className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">Drag to pan</span>
              </div>
            )}
          </div>
          
          {/* Tools Sidebar */}
          <div className="w-72 border-l border-border flex flex-col bg-background">
            <Tabs value={activeTool} onValueChange={setActiveTool} className="flex-1 flex flex-col">
              <TabsList className="w-full bg-muted rounded-none border-b border-border h-12 p-1">
                <TabsTrigger 
                  value="adjust" 
                  className="flex-1 data-[state=active]:bg-background"
                  data-testid="tab-adjust"
                >
                  <Sun className="w-4 h-4" />
                </TabsTrigger>
                <TabsTrigger 
                  value="transform" 
                  className="flex-1 data-[state=active]:bg-background"
                  data-testid="tab-transform"
                >
                  <RotateCw className="w-4 h-4" />
                </TabsTrigger>
                <TabsTrigger 
                  value="crop" 
                  className="flex-1 data-[state=active]:bg-background"
                  data-testid="tab-crop"
                >
                  <Crop className="w-4 h-4" />
                </TabsTrigger>
                <TabsTrigger 
                  value="resize" 
                  className="flex-1 data-[state=active]:bg-background"
                  data-testid="tab-resize"
                >
                  <Maximize2 className="w-4 h-4" />
                </TabsTrigger>
                <TabsTrigger 
                  value="ai" 
                  className="flex-1 data-[state=active]:bg-background"
                  data-testid="tab-ai"
                >
                  <Wand2 className="w-4 h-4" />
                </TabsTrigger>
              </TabsList>
              
              {/* Adjust Tab */}
              <TabsContent value="adjust" className="flex-1 m-0 p-4 space-y-6 overflow-y-auto">
                <div>
                  <Label className="text-xs text-muted-foreground flex items-center gap-2">
                    <Sun className="w-3 h-3" /> Brightness
                  </Label>
                  <div className="flex items-center gap-2 mt-2">
                    <Slider
                      value={[brightness]}
                      onValueChange={([v]) => setBrightness(v)}
                      min={0}
                      max={200}
                      step={1}
                      className="flex-1"
                      data-testid="brightness-slider"
                    />
                    <span className="w-10 text-xs text-right text-foreground">{brightness}%</span>
                  </div>
                </div>
                
                <div>
                  <Label className="text-xs text-muted-foreground flex items-center gap-2">
                    <Contrast className="w-3 h-3" /> Contrast
                  </Label>
                  <div className="flex items-center gap-2 mt-2">
                    <Slider
                      value={[contrast]}
                      onValueChange={([v]) => setContrast(v)}
                      min={0}
                      max={200}
                      step={1}
                      className="flex-1"
                      data-testid="contrast-slider"
                    />
                    <span className="w-10 text-xs text-right text-foreground">{contrast}%</span>
                  </div>
                </div>
                
                <div>
                  <Label className="text-xs text-muted-foreground flex items-center gap-2">
                    <Droplets className="w-3 h-3" /> Saturation
                  </Label>
                  <div className="flex items-center gap-2 mt-2">
                    <Slider
                      value={[saturation]}
                      onValueChange={([v]) => setSaturation(v)}
                      min={0}
                      max={200}
                      step={1}
                      className="flex-1"
                      data-testid="saturation-slider"
                    />
                    <span className="w-10 text-xs text-right text-foreground">{saturation}%</span>
                  </div>
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={resetAdjustments}
                  className="w-full border-border"
                >
                  Reset Adjustments
                </Button>
              </TabsContent>
              
              {/* Transform Tab */}
              <TabsContent value="transform" className="flex-1 m-0 p-4 space-y-6 overflow-y-auto">
                <div>
                  <Label className="text-xs text-muted-foreground">Zoom</Label>
                  <div className="flex items-center gap-2 mt-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="w-8 h-8"
                      onClick={() => setZoom(z => Math.max(0.5, z - 0.25))}
                      data-testid="zoom-out-btn"
                    >
                      <ZoomOut className="w-4 h-4" />
                    </Button>
                    <Slider
                      value={[zoom]}
                      onValueChange={([v]) => setZoom(v)}
                      min={0.5}
                      max={4}
                      step={0.1}
                      className="flex-1"
                      data-testid="zoom-slider"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="w-8 h-8"
                      onClick={() => setZoom(z => Math.min(4, z + 0.25))}
                      data-testid="zoom-in-btn"
                    >
                      <ZoomIn className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-center text-muted-foreground mt-1">{Math.round(zoom * 100)}%</p>
                  {zoom > 1 && (
                    <p className="text-xs text-center text-violet-500 mt-1">Drag image to pan</p>
                  )}
                </div>
                
                <div>
                  <Label className="text-xs text-muted-foreground">Rotation</Label>
                  <div className="flex items-center gap-2 mt-2">
                    <Slider
                      value={[rotation]}
                      onValueChange={([v]) => setRotation(v)}
                      min={0}
                      max={360}
                      step={1}
                      className="flex-1"
                      data-testid="rotation-slider"
                    />
                    <span className="w-10 text-xs text-right text-foreground">{rotation}°</span>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 border-border"
                      onClick={() => setRotation(r => (r + 90) % 360)}
                    >
                      <RotateCw className="w-4 h-4 mr-1" /> 90°
                    </Button>
                  </div>
                </div>
                
                <div>
                  <Label className="text-xs text-muted-foreground">Flip</Label>
                  <div className="flex gap-2 mt-2">
                    <Button
                      variant={flipH ? 'default' : 'outline'}
                      size="sm"
                      className={`flex-1 ${!flipH ? 'border-border' : ''}`}
                      onClick={() => setFlipH(!flipH)}
                      data-testid="flip-h-btn"
                    >
                      <FlipHorizontal className="w-4 h-4 mr-1" /> H
                    </Button>
                    <Button
                      variant={flipV ? 'default' : 'outline'}
                      size="sm"
                      className={`flex-1 ${!flipV ? 'border-border' : ''}`}
                      onClick={() => setFlipV(!flipV)}
                      data-testid="flip-v-btn"
                    >
                      <FlipVertical className="w-4 h-4 mr-1" /> V
                    </Button>
                  </div>
                </div>
              </TabsContent>
              
              {/* Crop Tab */}
              <TabsContent value="crop" className="flex-1 m-0 p-4 space-y-4 overflow-y-auto">
                <div className="text-center py-4">
                  <Crop className="w-12 h-12 mx-auto text-violet-500 mb-4" />
                  <h3 className="font-medium mb-2 text-foreground">Crop Image</h3>
                  <p className="text-xs text-muted-foreground mb-4">
                    Select an area to crop your image. Drag corners or edges to resize.
                  </p>
                  
                  {!isCropping ? (
                    <Button
                      onClick={startCropMode}
                      className="w-full bg-violet-600 hover:bg-violet-700"
                      data-testid="start-crop-btn"
                    >
                      <Crop className="w-4 h-4 mr-2" />
                      Start Cropping
                    </Button>
                  ) : (
                    <div className="space-y-3">
                      <div className="bg-muted rounded-lg p-3 text-sm">
                        <p className="text-muted-foreground">Current selection:</p>
                        <p className="font-mono text-foreground">
                          {Math.round(cropArea.width)} × {Math.round(cropArea.height)} px
                        </p>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Use the buttons below the image to apply or cancel
                      </p>
                    </div>
                  )}
                </div>
                
                <div className="border-t border-border pt-4">
                  <p className="text-xs text-muted-foreground mb-2">Original size:</p>
                  <p className="font-mono text-sm text-foreground">
                    {Math.round(imageDimensions.width)} × {Math.round(imageDimensions.height)} px
                  </p>
                </div>
              </TabsContent>
              
              {/* Resize Tab */}
              <TabsContent value="resize" className="flex-1 m-0 p-4 space-y-4 overflow-y-auto">
                <div className="text-center py-2">
                  <Maximize2 className="w-10 h-10 mx-auto text-violet-500 mb-3" />
                  <h3 className="font-medium mb-1 text-foreground">Resize Image</h3>
                  <p className="text-xs text-muted-foreground">
                    Change the dimensions of your image
                  </p>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Width (px)</Label>
                    <Input
                      type="number"
                      value={resizeWidth}
                      onChange={(e) => handleWidthChange(e.target.value)}
                      className="mt-1 bg-background border-border"
                      data-testid="resize-width-input"
                    />
                  </div>
                  
                  <div className="flex items-center justify-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setLockAspectRatio(!lockAspectRatio)}
                      className={`${lockAspectRatio ? 'text-violet-500' : 'text-muted-foreground'}`}
                      data-testid="lock-aspect-btn"
                    >
                      {lockAspectRatio ? (
                        <Lock className="w-4 h-4 mr-1" />
                      ) : (
                        <Unlock className="w-4 h-4 mr-1" />
                      )}
                      {lockAspectRatio ? 'Aspect Locked' : 'Aspect Unlocked'}
                    </Button>
                  </div>
                  
                  <div>
                    <Label className="text-xs text-muted-foreground">Height (px)</Label>
                    <Input
                      type="number"
                      value={resizeHeight}
                      onChange={(e) => handleHeightChange(e.target.value)}
                      className="mt-1 bg-background border-border"
                      data-testid="resize-height-input"
                    />
                  </div>
                  
                  <Button
                    onClick={applyResize}
                    disabled={resizeWidth <= 0 || resizeHeight <= 0}
                    className="w-full bg-violet-600 hover:bg-violet-700"
                    data-testid="apply-resize-btn"
                  >
                    Apply Resize
                  </Button>
                  
                  <div className="border-t border-border pt-4">
                    <p className="text-xs text-muted-foreground mb-2">Quick presets:</p>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-border text-xs"
                        onClick={() => {
                          setResizeWidth(1920);
                          setResizeHeight(lockAspectRatio ? Math.round(1920 / aspectRatio) : 1080);
                        }}
                      >
                        1920px
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-border text-xs"
                        onClick={() => {
                          setResizeWidth(1280);
                          setResizeHeight(lockAspectRatio ? Math.round(1280 / aspectRatio) : 720);
                        }}
                      >
                        1280px
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-border text-xs"
                        onClick={() => {
                          setResizeWidth(800);
                          setResizeHeight(lockAspectRatio ? Math.round(800 / aspectRatio) : 600);
                        }}
                      >
                        800px
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-border text-xs"
                        onClick={() => {
                          const halfW = Math.round(imageDimensions.width / 2);
                          setResizeWidth(halfW);
                          setResizeHeight(lockAspectRatio ? Math.round(halfW / aspectRatio) : Math.round(imageDimensions.height / 2));
                        }}
                      >
                        50%
                      </Button>
                    </div>
                  </div>
                </div>
              </TabsContent>
              
              {/* AI Tools Tab */}
              <TabsContent value="ai" className="flex-1 m-0 p-4 space-y-4 overflow-y-auto">
                <div className="text-center py-4">
                  <Wand2 className="w-12 h-12 mx-auto text-violet-500 mb-4" />
                  <h3 className="font-medium mb-2 text-foreground">AI Background Removal</h3>
                  <p className="text-xs text-muted-foreground mb-4">
                    Remove the background from your image using AI
                  </p>
                  <Button
                    onClick={handleRemoveBackground}
                    disabled={isRemovingBg || !imageData}
                    className="w-full bg-violet-600 hover:bg-violet-700"
                    data-testid="remove-bg-btn"
                  >
                    {isRemovingBg ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Wand2 className="w-4 h-4 mr-2" />
                        Remove Background
                      </>
                    )}
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
